import React, { useState, useEffect } from 'react';
import {
  Typography, Button, Box, Alert, CircularProgress, TextField,
} from '@mui/material';
import ModalWrapper from '../components/ModalWrapper';
import { apiFetch } from '../apiFetch';

interface AccountFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (data: { first_name: string; last_name: string }) => void;
  currentFirstName: string;
  currentLastName: string;
}

const AccountFormModal: React.FC<AccountFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  currentFirstName,
  currentLastName,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Prefill form when modal opens
  useEffect(() => {
    if (open) {
      setFirstName(currentFirstName);
      setLastName(currentLastName);
      setErrors({});
      setApiError(null);
    }
  }, [open, currentFirstName, currentLastName]);

  const validate = (): boolean => {
    const newErrors: { firstName?: string; lastName?: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setApiError(null);

    try {
      const data = await apiFetch(`${apiUrl}/api/account/profile/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        }),
      });

      // Refresh the access token to get updated claims
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        const tokenResponse = await fetch(`${apiUrl}/api/token/refresh/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshToken }),
        });

        if (tokenResponse.ok) {
          const tokens = await tokenResponse.json();
          localStorage.setItem('access_token', tokens.access);
          // Notify Header of the change
          window.dispatchEvent(new Event('authChange'));
        }
      }

      onSuccess({ first_name: data.first_name, last_name: data.last_name });
      onClose();
    } catch (err: any) {
      setApiError(err.message || 'An error occurred while updating your profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <Typography variant="h6" gutterBottom>
        Editing Account Details
      </Typography>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <TextField
          label="First Name"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            if (errors.firstName) {
              setErrors((prev) => ({ ...prev, firstName: undefined }));
            }
          }}
          required
          fullWidth
          disabled={loading}
          error={!!errors.firstName}
          helperText={errors.firstName}
        />
        <TextField
          label="Last Name"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            if (errors.lastName) {
              setErrors((prev) => ({ ...prev, lastName: undefined }));
            }
          }}
          required
          fullWidth
          disabled={loading}
          error={!!errors.lastName}
          helperText={errors.lastName}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" color="primary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </Box>
    </ModalWrapper>
  );
};

export default AccountFormModal;
